<homey-page-title title="Notification preferences" class="mb-1"/>

@if (loading() || !settings()) {
  <div class="space-y-2">
    @for (_ of [0,1,2,3]; track _) {
      <div class="flex flex-row justify-between items-center py-2">
        <div class="space-y-2 w-full pr-4">
          <p-skeleton height="1rem" width="40%"></p-skeleton>
          <p-skeleton height="0.9rem" width="55%"></p-skeleton>
        </div>
        <p-skeleton height="1.5rem" width="3.5rem" styleClass="flex-shrink-0"></p-skeleton>
      </div>
      <p-divider class="p-0 m-0"/>
    }
  </div>
} @else {
  <div class="flex flex-row justify-between items-center py-2">
    <div class="flex flex-col">
      <label for="pushNotificationsEnabled" [ngClass]="{
       'text-surface-400': !isNative
     }">Push notifications enabled</label>
      @if (!isNative) {
        <p class="text-sm text-surface-500 m-0 mt-1">Only in our mobile app. <a class="underline" [href]="iosAppUrl" target="_blank" rel="noopener">iOS</a> · <a class="underline" [href]="androidAppUrl" target="_blank" rel="noopener">Android</a></p>
      }
    </div>
    <p-toggleswitch inputId="pushNotificationsEnabled"
                    class="flex"
                    [disabled]="!isNative || loading()"
                    (onChange)="onPushNotionEnabled($event)"
                    [ngModel]="form.controls.pushNotificationsEnabled.value"/>
  </div>
  <p-divider class="p-0 m-0"/>
  <div class="flex flex-row justify-between items-center">
    <div class="flex flex-col" >
      <label for="emailNotificationsEnabled">Email notifications</label>
      @if (form.getRawValue().emailNotificationsEnabled) {
      <div class="flex flex-row items-center gap-2 text-sm text-surface-600 min-h-[24px]">
        <p class="m-0 text-surface-500 break-all">
          {{ form.controls.email.value }}
        </p>
        <p-button size="small" severity="secondary" [text]="true" [style]="{padding: 0}" icon="pi pi-pencil" (onClick)="openEmailEdit()"/>
      </div>
      }
      @if (!form.getRawValue().emailNotificationsEnabled) {
        <p class="text-sm text-surface-500 m-0 mt-1">We still send critical updates and lottery winnings.</p>
      }
    </div>
    <p-toggleswitch inputId="emailNotificationsEnabled"
                    [disabled]="loading()"
                    class="flex"
                    [formControl]="form.controls.emailNotificationsEnabled"/>
  </div>
  <p-divider class="p-0 m-0"/>
  <div class="flex flex-row justify-between items-center" [class.py-2]="!form.getRawValue().smsNotificationsEnabled">
    <div class="flex flex-col">
      <label for="smsNotificationsEnabled">Text message notifications</label>
      @if (form.getRawValue().smsNotificationsEnabled) {
        <div class="flex flex-row items-center gap-2 text-sm text-surface-600 min-h-[24px]">
          <p class="m-0 text-surface-500 break-all">
            {{ form.controls.phone.value || 'No phone number set' }}
          </p>
          <p-button size="small" severity="secondary" [text]="true" [style]="{padding: 0}" icon="pi pi-pencil" (onClick)="openPhoneEdit()"/>
        </div>
      }
    </div>
    <p-toggleswitch inputId="smsNotificationsEnabled"
                    [disabled]="loading()"
                    class="flex"
                    (onChange)="sendSmsNotificationEnabled($event)"
                    [formControl]="form.controls.smsNotificationsEnabled"/>
  </div>
  <p-divider class="p-0 m-0"/>
  <div class="flex flex-row justify-between py-2">
    <label for="nonLotteryNotificationsEnabled">Send non-lottery related messages</label>
    <p-toggleswitch inputId="nonLotteryNotificationsEnabled"
                    [disabled]="loading()"
                    class="flex"
                    [formControl]="form.controls.nonLotteryNotificationsEnabled"/>
  </div>
}

<p-dialog header="Text message notifications" [(visible)]="enableSmsPopupVisible" modal="true" [closable]="true" (onHide)="onHide()" styleClass="max-w-lg">
  <p class="text-muted-color">Would you like to receive updates about your applications, new lotteries you qualify for, and Homey via text message?</p>
  <p-floatlabel variant="on" class="mt-6">
    <p-inputmask inputId="phone-enable" class="w-full p-inputwrapper-filled" styleClass="w-full" mask="(999) 999-9999" [formControl]="form.controls.phone" />
    <label for="phone-enable">Phone Number</label>
  </p-floatlabel>
  @if (form.controls.phone.invalid && form.controls.phone.touched) {
    <p class="text-red-500 text-sm mt-2">Enter a valid 10-digit phone number.</p>
  }
  <ng-template #footer>
    <div class="flex flex-row justify-center w-full">
      <homey-submit-button size="small" submitButtonLabel="Send me text updates" [submitFunction]="submitPhoneNumber"></homey-submit-button>
    </div>
  </ng-template>
</p-dialog>

<p-dialog header="Enable notifications in settings"
          [(visible)]="pushPermissionDeniedDialogVisible"
          modal="true"
          [closable]="true"
          styleClass="max-w-lg">
  <p class="text-muted-color">
    We can’t enable push notifications until you allow them in your device settings. Open settings and turn on notifications for Homey, then try again.
  </p>
  <ng-template #footer>
    <div class="flex flex-row justify-between w-full">
      <p-button label="Not now" severity="secondary" [text]="true" (onClick)="pushPermissionDeniedDialogVisible.set(false)"/>
      <p-button label="Open settings" (onClick)="pushNotificationService.openNotificationSettings(); pushPermissionDeniedDialogVisible.set(false)"/>
    </div>
  </ng-template>
</p-dialog>

<p-dialog header="Edit phone number" [(visible)]="editSmsPopupVisible" modal="true" [closable]="true" (onHide)="editSmsPopupVisible.set(false)" styleClass="max-w-lg">
  <p class="text-muted-color">Update the phone number we use for application updates and new lotteries.</p>
  <p-floatlabel variant="on" class="mt-6">
    <p-inputmask inputId="phone-edit" class="w-full p-inputwrapper-filled" styleClass="w-full" mask="(999) 999-9999" [formControl]="form.controls.phone" />
    <label for="phone-edit">Phone Number</label>
  </p-floatlabel>
  @if (form.controls.phone.invalid && form.controls.phone.touched) {
    <p class="text-red-500 text-sm mt-2">Enter a valid 10-digit phone number.</p>
  }
  <ng-template #footer>
    <div class="flex flex-row justify-center w-full">
      <homey-submit-button size="small" submitButtonLabel="Save phone number" [submitFunction]="submitPhoneNumber"></homey-submit-button>
    </div>
  </ng-template>
</p-dialog>

<p-dialog header="Email address" [(visible)]="emailPopupVisible" modal="true" [closable]="true" styleClass="max-w-lg">
  <p class="text-muted-color">Choose which email we should use for application updates and new lotteries.</p>
  <p-floatlabel variant="on" class="mt-6">
    <input pInputText id="email" class="w-full" [formControl]="form.controls.email" type="email" autocomplete="email">
    <label for="email">Email address</label>
  </p-floatlabel>
  @if (form.controls.email.invalid && form.controls.email.touched) {
    <p class="text-red-500 text-sm mt-2">Enter a valid email address.</p>
  }
  <ng-template #footer>
    <div class="flex flex-row justify-center w-full">
      <homey-submit-button size="small" submitButtonLabel="Save email" [submitFunction]="submitEmailAddress"></homey-submit-button>
    </div>
  </ng-template>
</p-dialog>
